import React, { useEffect, useState } from "react";
import { toast, Toaster } from "sonner";
import PublicNavbar from "../components/layout/PublicNavbar";
import Footer from "../components/layout/Footer";
import StartupCard from "../components/marketplace/StartupCard";
import { Select, Input } from "../components/common/Primitives";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import Seo from "../components/Seo";

const sectors = ["", "Fintech", "CleanTech", "HealthTech", "SaaS", "Consumer", "MSME", "Manufacturing", "Enterprise SaaS"];
const stages = ["", "Idea", "Pre-Seed", "Seed", "Pre-Series A", "Series A", "Series B", "Growth"];
const fundingTypes = ["", "Equity", "Debt", "Hybrid", "Convertible"];

export default function Marketplace() {
  const { user } = useAuth();
  const [startups, setStartups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ sector: "", stage: "", funding_type: "", search: "" });
  const [stats, setStats] = useState({ total_startups: 0, total_investors: 0, total_funding_demand_cr: 0 });

  const load = () => {
    setLoading(true);
    const params = {};
    if (filters.sector) params.sector = filters.sector;
    if (filters.stage) params.stage = filters.stage;
    if (filters.funding_type) params.funding_type = filters.funding_type;
    api.get("/marketplace/startups", { params })
      .then((r) => setStartups(r.data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  };
  useEffect(load, [filters.sector, filters.stage, filters.funding_type]);
  useEffect(() => { api.get("/marketplace/stats").then((r) => setStats(r.data)).catch(() => {}); }, []);

  // Sector-report nurture beacon — if a lead lands here via an ?srt=… link
  // in a nurture email, tell the backend so future touches stop.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const srt = params.get("srt");
    if (!srt) return;
    const beaconKey = `vz_srt_${srt}`;
    if (sessionStorage.getItem(beaconKey) === "1") return;
    api.post("/public/sector-reports/lead-visit", { token: srt })
      .then(() => { try { sessionStorage.setItem(beaconKey, "1"); } catch { /* ignore */ } })
      .catch(() => { /* silent — non-critical */ });
  }, []);

  const filtered = startups.filter((s) =>
    !filters.search || (s.startup_name || "").toLowerCase().includes(filters.search.toLowerCase()) || (s.sector || "").toLowerCase().includes(filters.search.toLowerCase())
  );

  const express = async (startup) => {
    if (!user) {
      toast.error("Please sign in as an approved investor to express interest.");
      return;
    }
    if (user.role !== "investor") {
      toast.error("Only investors can express interest.");
      return;
    }
    try {
      await api.post("/investor/interest", { startup_id: startup.startup_id });
      toast.success("Interest expressed. Admin has been notified.");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="min-h-screen bg-transparent text-ink">
      <Seo
        title="Verified Directory | Discover Vetted Startups on VenturaZ"
        description="Browse verified, high-signal startups by sector, stage and funding type. VenturaZ's marketplace connects investors with founders ready for the next round."
        path="/marketplace"
      />
      <Toaster richColors position="top-right" />
      <PublicNavbar />
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16" data-testid="marketplace-page">
        <div className="grid grid-cols-1 md:grid-cols-2 items-end gap-6 mb-10">
          <div>
            <div className="overline mb-3">CURATED DIRECTORY</div>
            <h1 className="font-display text-4xl lg:text-5xl text-ink leading-tight">Live investable opportunities.</h1>
            <p className="text-ink-muted mt-4 max-w-xl">Every listing is admin-reviewed. Sensitive fields unlock only for approved investors.</p>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <MarketStat label="Startups" value={stats.total_startups} />
            <MarketStat label="Investors" value={stats.total_investors} />
            <MarketStat label="Demand" value={`₹${stats.total_funding_demand_cr}Cr`} />
          </div>
        </div>

        <div className="glass rounded-lg p-4 grid grid-cols-1 md:grid-cols-4 gap-3 mb-8" data-testid="marketplace-filters">
          <Input placeholder="Search sector or name" value={filters.search} onChange={(e) => setFilters({ ...filters, search: e.target.value })} testId="filter-search" />
          <Select value={filters.sector} onChange={(e) => setFilters({ ...filters, sector: e.target.value })} testId="filter-sector">
            {sectors.map((s) => <option key={s} value={s}>{s || "All sectors"}</option>)}
          </Select>
          <Select value={filters.stage} onChange={(e) => setFilters({ ...filters, stage: e.target.value })} testId="filter-stage">
            {stages.map((s) => <option key={s} value={s}>{s || "All stages"}</option>)}
          </Select>
          <Select value={filters.funding_type} onChange={(e) => setFilters({ ...filters, funding_type: e.target.value })} testId="filter-funding">
            {fundingTypes.map((s) => <option key={s} value={s}>{s || "All funding types"}</option>)}
          </Select>
        </div>

        {loading ? (
          <div className="text-ink-muted text-sm">Loading opportunities…</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="marketplace-grid">
            {filtered.map((s) => (
              <StartupCard key={s.startup_id} startup={s} onExpressInterest={user?.role === "investor" ? express : undefined} />
            ))}
            {filtered.length === 0 && (
              <div className="col-span-full border border-dashed border-white/15 p-10 text-center text-ink-muted">No startups match your filters.</div>
            )}
          </div>
        )}
      </section>
      <Footer />
    </div>
  );
}

function MarketStat({ label, value }) {
  return (
    <div className="glass rounded-md p-3">
      <div className="overline">{label}</div>
      <div className="font-heading text-ink text-xl mt-0.5">{value}</div>
    </div>
  );
}
